// Angular
import {
	Component, ComponentRef, ElementRef, Input, OnChanges, SimpleChanges,
	ViewChild,
	ViewContainerRef,
	ViewEncapsulation
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
// Model
import { SubContentModel } from '../../model/sub-content.model';
import { SubContent } from '../../model/sub-content.interface';
// Directive
import { DynamicContentDirective } from '../dynamic-component/dynamic-content.directive';
// Service
import { SubContentService } from '../../service/sub-content.service';

@Component({
	selector: 'cv-sub-content',
	encapsulation: ViewEncapsulation.None,
	templateUrl: './sub-content.component.html',
	styleUrls: ['./sub-content.component.scss'],
	animations: [
		trigger('showHide', [
			state('show', style({ bottom: '10%', opacity: '1'})),
			state('hide', style({ bottom: '-10%', opacity: '0'})),
			transition(`hide <=> show`,
				animate('300ms ease-in-out'))
		])
	]
})
export class SubContentComponent implements OnChanges {
	@ViewChild(DynamicContentDirective, { static: true }) dynamicContent: DynamicContentDirective;
	@Input() isSubContentPanelOpen = false;

	private currentViewContainerRef: ViewContainerRef;
	private currentSubContentInstance: SubContent;
	private componentRef: ComponentRef<any>;

	displayNotification: boolean;
	notificationMessage: string;
	notificationWidth: number;

	constructor(
		private elementRef: ElementRef,
		private subContentService: SubContentService
	) {
		this.notificationMessage = '';
		this.registerSubContent();
	}

	/**
	 * Ensure the SubContent Panel is Open or Close
	 * @param changes
	 */
	ngOnChanges(changes: SimpleChanges): void {
		this.isSubContentPanelOpen = changes.isSubContentPanelOpen.currentValue;
		if (this.currentViewContainerRef && this.currentSubContentInstance) {
			this.currentSubContentInstance.isSubContentPanelOpen = this.isSubContentPanelOpen;
		}
	}

	/**
   * Register the event, so it can be listen to on open events or clear current instance
   */
	public registerSubContent(): void {
		this.subContentService.onOpen((event: SubContentModel) => {
			this.createComponent(event);
		});
		this.subContentService.onClose((event: SubContentModel) => {
			this.clearContent();
		});
	}

	/**
   * Create the component and clear previous data on it
   * @param componentFactoryResolver
   */
	public createComponent(event: SubContentModel): void {
		// Create new instance
		const componentFactory = event.componentFactoryResolver.resolveComponentFactory(
			event.component
		);
		this.currentViewContainerRef = this.dynamicContent.viewContainerRef;
		// clear out and "destroy" current components.
		if (this.componentRef) {
			this.componentRef.destroy();
			this.componentRef = null;
		}
		this.currentViewContainerRef.remove();
		this.currentViewContainerRef.clear();
		this.componentRef = this.currentViewContainerRef.createComponent(componentFactory);
		this.currentSubContentInstance = <SubContent>this.componentRef.instance;
		this.currentSubContentInstance.isSubContentPanelOpen = this.isSubContentPanelOpen;
		this.currentSubContentInstance.data = event.data;
		if (this.currentSubContentInstance.successEvent) {
			this.currentSubContentInstance.successEvent.subscribe(result =>
				this.subContentService.onSuccess(result)
			);
		}
		if (this.currentSubContentInstance.errorEvent) {
			this.currentSubContentInstance.errorEvent.subscribe(result =>
				this.subContentService.onError(result)
			);
		}
		if (this.currentSubContentInstance.updateEvent) {
			this.currentSubContentInstance.updateEvent.subscribe(result =>
				this.subContentService.onUpdate(result)
			);
		}
		if (this.currentSubContentInstance.notificationEvent) {
			this.currentSubContentInstance.notificationEvent.subscribe(result =>
				this.showNotification(result)
			);
		}
		setTimeout(() => {
			this.currentSubContentInstance.elementRef = this.elementRef;
		});
	}

	/**
   * Clear current instance
   */
	public clearContent(): void {
		if (this.currentSubContentInstance) {
			this.currentSubContentInstance.closeSubContent();
		}
		if (this.currentViewContainerRef) {
			this.currentViewContainerRef.clear();
		}
	}

	/**
	 * Displays a notification message.
 	 * @param message
	 */
	private showNotification(message: string): void {
		this.notificationMessage = message;
		this.notificationWidth = this.elementRef.nativeElement.offsetWidth - 10;
		this.displayNotification = true;
		setTimeout(() => this.displayNotification = false, 2500);
	}
}
